import { Box, Stack, useTheme } from '@mui/material';
import { noop } from 'lodash';
import { ReactNode } from 'react';

import { PresentationSlide } from '@/modules/presentation/PresentationSlide';

import { useMergedSlideDesign } from '../hooks/useMergedSlideDesign';
import {
  SLIDE_CONTENT_TOP_OFFSET_SPACING,
  SLIDE_HERO_CONTENT_TOP_OFFSET_SPACING,
  SLIDE_TITLE_TO_CONTENT_SPACING,
} from '../shared/slideContentSpacing';
import {
  CustomPositionedSlideLogo,
  getSlideLogoState,
  SlideLogomark,
} from '../shared/slideLogo';
import { getSlideTextHorizontalPosition } from '../shared/slideTextPosition';
import { useInlineLogoHaloExtension } from '../shared/useInlineLogoHaloExtension';
import { SlideEditorCoverPage } from '../SlideEditor.types';
import { SlideEditorPromptButton } from '../textEditors/SlideEditor.promptButton';
import {
  SlideEditorPromptProvider,
  usePromptDisplayContent,
} from '../textEditors/SlideEditor.promptContext';
import { SlideEditorRichTextEditor } from '../textEditors/SlideEditor.richTextEditor';
import { SlideEditorTitleTextEditor } from '../textEditors/SlideEditor.titleTextEditor';
import { SlideEditorVariableButton } from '../textEditors/SlideEditor.variableButton';
import {
  PresentationCoverSlideConfig,
  SlideConfigVariant,
  SlideHoverHaloRefs,
  TenantSlideStyle,
} from './CoverSlide.types';

// Placeholder shown in the designer when the cover slide title is blank.
// In the read-only print view a blank title renders nothing (the title
// editor returns null), so this only ever surfaces while editing. [T1-6300]
export const COVER_SLIDE_TITLE_PLACEHOLDER = 'Click to add title';

export interface CustomSlideProps {
  Slide?: typeof PresentationSlide.Slide;
  /** page data (body) */
  page: SlideEditorCoverPage['page'];
  /** bundle-level data */
  bundle: SlideEditorCoverPage['bundle'];
  onCustomContentChange?: (newCustomContent: string) => void;
  onDisplayTitleChange?: (newDisplayName: string) => void;
  /**
   * Per-keystroke variants of the change handlers above, each forwarding the
   * editor's current value (unlike the `onChange` handlers, which commit on
   * blur). The template editor uses these to track in-progress edits and arm
   * the unsaved-changes blocker before the user navigates away.
   */
  onDisplayTitleInput?: (value: string) => void;
  onCustomContentInput?: (value: string) => void;
  onClickInput?: () => void;
  onPromptChange?: (prompt: string | null) => void;
  onContentGenerated?: (content: string) => void;
  highlightVariables?: boolean;
  HeaderComponent?: ReactNode;
  /** Optional prop-level design overrides (highest priority in merge). */
  tenantSlideDesign?: PresentationCoverSlideConfig;
  /** Slide ID for scroll-to targeting. */
  slideId?: string;
  /** Optional footer rendered at the bottom of the slide. */
  footer?: ReactNode;
  /**
   * Optional content rendered inside the slide's clipped content area
   * alongside the title block — used by the admin branding preview to
   * mount drag overlays that need to live inside the slide's scale
   * transform so their coordinates align with the title-block math.
   */
  slideOverlay?: ReactNode;
  /**
   * Optional refs to internal elements that the admin branding preview
   * uses to drive its hover-halo indicator. See `SlideHoverHaloRefs`
   * for details. Outside of the admin preview this is omitted.
   */
  hoverHaloRefs?: SlideHoverHaloRefs;
}

/**
 * Standalone cover slide.
 *
 * Renders PresentationSlide.Slide internally with background props
 * derived from the merged design config (prop > tenant > defaults).
 */
export function CoverSlide({
  page,
  onCustomContentChange,
  onDisplayTitleChange,
  onDisplayTitleInput,
  onCustomContentInput,
  onClickInput,
  onPromptChange,
  onContentGenerated,
  highlightVariables,
  HeaderComponent,
  tenantSlideDesign: tenantSlideDesignProp,
  slideId,
  footer,
  Slide = PresentationSlide.Slide,
  slideOverlay,
  hoverHaloRefs,
}: CustomSlideProps) {
  const theme = useTheme();
  const { customContent, displayTitle, customPrompt } = page;

  const { displayContent, editableContent, handleRTESubmit } =
    usePromptDisplayContent({
      content: customContent,
      customPrompt,
      onContentChange: onCustomContentChange,
      onPromptChange,
    });

  // Merge prop > tenant config > defaults at the field level.
  // Waterfall + waterfall-comparison covers (callers that supply a
  // fixed HeaderComponent) opt out of tenant cover branding entirely:
  // no background, no text-color/font-size overrides, no body logo,
  // the title block sits at its default anchor, and the footer is
  // always shown regardless of the tenant's includeFooter flag.
  const mergedTenantSlideDesign = useMergedSlideDesign(
    SlideConfigVariant.COVER,
    tenantSlideDesignProp
  );
  const tenantSlideDesign = HeaderComponent
    ? undefined
    : mergedTenantSlideDesign;

  const tenantStyle: TenantSlideStyle | undefined =
    tenantSlideDesign?.textColor ||
    tenantSlideDesign?.titleTextSize ||
    tenantSlideDesign?.titleSizeRem != null ||
    tenantSlideDesign?.bodyTextColor ||
    tenantSlideDesign?.bodyTextAlignment
      ? {
          textColor: tenantSlideDesign.textColor,
          titleTextSize: tenantSlideDesign.titleTextSize,
          titleSizeRem: tenantSlideDesign.titleSizeRem,
          bodyTextColor: tenantSlideDesign.bodyTextColor,
          bodyTextAlignment: tenantSlideDesign.bodyTextAlignment,
        }
      : undefined;

  // T1-5959: continuous logo scaling (replaces the 5-bucket logoSize
  // enum) and an opt-out for the default logo-anchored-to-title layout.
  // Shared with DividerSlide so both stay in lockstep.
  const {
    logoScaleFactor,
    useDefaultLogoPosition,
    logoOriginX,
    logoOriginY,
    textOriginX,
    textOriginY,
  } = getSlideLogoState(tenantSlideDesign);

  // When the admin opts out of showing the logo, suppress every logo
  // render path — inline slot, custom-positioned variant, and the
  // reserved vertical space that normally keeps the title baseline
  // stable when toggling the default-position checkbox.
  const hideLogo = tenantSlideDesign?.hideLogo === true;

  const textHorizontalPosition = getSlideTextHorizontalPosition(
    theme.spacing(10.5),
    textOriginX,
    tenantSlideDesign?.bodyTextAlignment,
    tenantSlideDesign?.textWidth
  );

  // Used only as the hidden placeholder when `useDefaultLogoPosition` is
  // false. Matches the visible inline logo's `enforceMaxHeight` so the
  // reserved layout space lines up with what the visible logo would
  // occupy — toggling the checkbox keeps the title baseline still.
  const logoElement = (
    <SlideLogomark
      scaleFactor={logoScaleFactor}
      enforceMaxHeight
      useDarkLogo={tenantSlideDesign?.useDarkLogo}
    />
  );

  // When the inline logo is scaled up, its CSS transform extends the
  // visible image above the wrapper's natural top. The admin's hover
  // halo needs to cover that extension as part of a single
  // "logo + title" rectangle, so we extend the halo target's box
  // upward by the scaled amount. Negative margin shifts the outer
  // box up; matching positive padding keeps the children at their
  // original positions. Outline is painted at the post-margin box,
  // so it wraps the scaled logo + title as a single rectangle.
  //
  // Only enabled in the admin preview (where `hoverHaloRefs` is
  // provided) and when the inline logo is actually rendered. Non-admin
  // contexts skip the measurement entirely.
  const { ref: inlineLogoRef, extensionPx: haloScaleExtensionPx } =
    useInlineLogoHaloExtension({
      enabled:
        !!hoverHaloRefs &&
        !HeaderComponent &&
        useDefaultLogoPosition &&
        !hideLogo,
      scaleFactor: logoScaleFactor,
    });

  const content = (
    <Stack
      spacing={2}
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        pointerEvents: 'auto',
      }}
    >
      <Stack
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'clip',
        }}
      >
        {!useDefaultLogoPosition && !hideLogo && tenantSlideDesign && (
          <CustomPositionedSlideLogo
            ref={hoverHaloRefs?.customLogo}
            slideName="CoverSlide"
            useDarkLogo={tenantSlideDesign.useDarkLogo}
            scaleFactor={logoScaleFactor}
            logoOriginX={logoOriginX}
            logoOriginY={logoOriginY}
          />
        )}

        {/* Title block: calc-based positioning lets left-aligned content
            wrap as textOriginX changes its available width. Right-aligned
            content moves both edges so its visible, anchored edge follows
            the drag origin. The Y axis uses the same coordinate model.

            When `HeaderComponent` is provided (e.g. a CompoundHeader
            with `h1_display`), the title is taller than the inline
            SlideEditorTitleTextEditor path (Romie xxl 48/62 vs the
            ramp's Medium xl 36/48), so we swap to the trimmed hero
            offset to keep body content at the same vertical slot. */}
        <Stack
          data-name="CoverSlide.TitleBlock"
          sx={{
            position: 'absolute',
            top: `calc(${theme.spacing(
              HeaderComponent
                ? SLIDE_HERO_CONTENT_TOP_OFFSET_SPACING
                : SLIDE_CONTENT_TOP_OFFSET_SPACING
            )} + ${textOriginY}px)`,
            ...textHorizontalPosition,
          }}
        >
          {/* Invisible placeholder when the admin has opted out of the
              default logo position. Lives outside the halo target so
              the admin hover halo wraps only the visible content while
              still reserving the logo's vertical space here, keeping
              the title baseline stable when the checkbox is toggled. */}
          {!useDefaultLogoPosition && !hideLogo && !HeaderComponent && (
            <Box mb="3vh !important" sx={{ visibility: 'hidden' }}>
              {logoElement}
            </Box>
          )}
          <Box
            ref={hoverHaloRefs?.titleHaloTarget}
            data-name="CoverSlide.TitleHaloTarget"
            sx={
              haloScaleExtensionPx > 0
                ? {
                    marginTop: `-${haloScaleExtensionPx}px`,
                    paddingTop: `${haloScaleExtensionPx}px`,
                  }
                : undefined
            }
          >
            <Stack direction="column" spacing={theme.spacing(1)}>
              {HeaderComponent ?? (
                <>
                  {/* Visible inline logo — only rendered when the
                      default position is on. When off, the sibling
                      placeholder above reserves the space. The scale
                      transform is applied here on the wrapper (with
                      `applyScale={false}` on the inner Logomark) so
                      the wrapper's visual is positioned correctly
                      from the bottom-left anchor while the layout box
                      stays at natural bounds. The hover halo wraps
                      the parent `TitleHaloTarget`, which the slide
                      extends upward to match the scaled visual when
                      this case is active. */}
                  {useDefaultLogoPosition && !hideLogo && (
                    <Box
                      ref={inlineLogoRef}
                      mb="3vh !important"
                      sx={{
                        transform: `scale(${logoScaleFactor})`,
                        transformOrigin: 'left bottom',
                      }}
                    >
                      <SlideLogomark
                        useDarkLogo={tenantSlideDesign?.useDarkLogo}
                        scaleFactor={logoScaleFactor}
                        applyScale={false}
                        enforceMaxHeight
                      />
                    </Box>
                  )}
                  {/* T1-5995: zIndex sits on the text wrapper (not the
                      title-block container) so the custom-positioned logo
                      only renders behind the text, never behind the
                      default-position logo above. */}
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <SlideEditorTitleTextEditor
                      title={displayTitle}
                      placeholder={COVER_SLIDE_TITLE_PLACEHOLDER}
                      onChange={onDisplayTitleChange}
                      onInput={onDisplayTitleInput}
                      onClickInput={onClickInput}
                      showSuggestions
                      highlightVariables={highlightVariables}
                      tenantStyle={tenantStyle}
                      allowLineBreaks
                    />
                  </Box>
                </>
              )}
            </Stack>
            <Box
              mt={`${SLIDE_TITLE_TO_CONTENT_SPACING} !important`}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <SlideEditorPromptProvider
                customPrompt={customPrompt ?? null}
                pageContent={customContent}
                onPromptChange={onPromptChange ?? noop}
                onContentGenerated={onContentGenerated ?? onCustomContentChange}
              >
                <SlideEditorRichTextEditor
                  content={displayContent}
                  editableContent={editableContent}
                  onSubmit={handleRTESubmit}
                  onRawUpdate={onCustomContentInput}
                  onClick={onClickInput}
                  highlightVariables={highlightVariables}
                  tenantStyle={tenantStyle}
                  additionalToolbarButtons={
                    <>
                      <SlideEditorPromptButton />
                      <SlideEditorVariableButton />
                    </>
                  }
                />
              </SlideEditorPromptProvider>
            </Box>
          </Box>
        </Stack>
        {slideOverlay}
      </Stack>
    </Stack>
  );

  // When a slideId is provided, CoverSlide owns the PresentationSlide.Slide
  // wrapper so it can pass background props from the merged design config.
  // When no slideId is provided (e.g. template editor), the caller wraps
  // in PresentationSlide.Slide externally.
  if (slideId) {
    return (
      <Slide
        id={slideId}
        slideDesign={tenantSlideDesign ?? {}}
        slideBackgroundColor={tenantSlideDesign?.backgroundColor}
        slideBackgroundImage={tenantSlideDesign?.backgroundImagePath}
        slideBackgroundAlpha={tenantSlideDesign?.backgroundImageAlpha}
        slideBackgroundOriginX={tenantSlideDesign?.backgroundImageOriginX}
        slideBackgroundOriginY={tenantSlideDesign?.backgroundImageOriginY}
        slideBackgroundScale={tenantSlideDesign?.backgroundImagePercentScale}
        footer={tenantSlideDesign?.includeFooter === false ? null : footer}
      >
        {content}
      </Slide>
    );
  }

  return content;
}
